import { useDisclosure } from '@chakra-ui/core'
import { RootState } from 'constants/interfaces'
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { handleSubmitPop, removeLinkPopLocal, removePopLocal, setDummyPop } from 'redux/actions/pops'
import { PopType } from './utils'
import { GPTPop } from './GPTPop'
import { DefaultPop } from './DefaultPOP'
import { RePop } from './RePop'
import { AIPop } from './AI/Pop'
import { AiRepop } from './AI/Repop'
import { ActionPop } from './ActionPop'
import { NotificationPopPlaceholder } from './NotificationPopPlaceholder'

export default function PopModal() {
  const dispatch = useDispatch()
  const { pops, dummyPop, isLoading } = useSelector((state: RootState) => state.pops)
  const { retailConfig, isConfigReady } = useSelector((state: RootState) => state.config)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const firstPopInStack = pops?.[0] ?? dummyPop
  const isNotificationPlaceholder = !!(firstPopInStack as any)?.isNotificationPlaceholder
  const displayedPopRef = useRef(firstPopInStack)

  displayedPopRef.current = firstPopInStack

  const acknowledgeLinkPop = () => {
    if (firstPopInStack === dummyPop || firstPopInStack.isPreview) return true

    const popId = firstPopInStack.pk ?? firstPopInStack.sk

    if (!popId) return false

    return dispatch<any>(handleSubmitPop({ pop_id: popId, isRepop: false, pop_type: PopType.LINK_POP }))
  }

  const handleSubmit = (popType: number | undefined) => {
    if (popType === PopType.DUMMY_POP) {
      dispatch(setDummyPop(null))
    } else if (popType === PopType.LINK_POP) {
      const popId = firstPopInStack.pk ?? firstPopInStack.sk
      const displayedPop = displayedPopRef.current

      if (
        (displayedPop?.pk ?? displayedPop?.sk) !== popId ||
        displayedPop?.pop_type !== PopType.LINK_POP ||
        displayedPop?.pop_body !== firstPopInStack.pop_body ||
        !!displayedPop?.isPreview !== !!firstPopInStack.isPreview
      )
        return false

      if (firstPopInStack === dummyPop) {
        dispatch(setDummyPop(null))
      } else if (!popId || !dispatch<any>(removeLinkPopLocal(firstPopInStack))) {
        return false
      }
    } else {
      const pop_type = firstPopInStack.pop_type
      const isRepop = popType === PopType.RE_POP || popType === PopType.AI_RE_POP
      dispatch(handleSubmitPop({ pop_id: firstPopInStack.pk ?? '', isRepop, pop_type }))
    }
    onClose()
    return true
  }

  const handleCloseNotificationPlaceholder = () => {
    const popId = (firstPopInStack as any)?.pk ?? (firstPopInStack as any)?.sk

    if (popId) {
      dispatch(removePopLocal(popId))
    }

    onClose()
  }

  useEffect(() => {
    if (pops.length > 0 && (!isLoading || isNotificationPlaceholder)) {
      onOpen()
      const interval = retailConfig?.pop_config.interval
      const timeoutId = setTimeout(onOpen, isNotificationPlaceholder ? 0 : interval)
      return () => clearTimeout(timeoutId)
    } else if (dummyPop !== null && !isLoading) {
      onOpen()
    }
  }, [dummyPop, isConfigReady, isLoading, isNotificationPlaceholder, onClose, onOpen, pops.length, retailConfig])

  return (
    <>
      {firstPopInStack && isNotificationPlaceholder && isOpen && (
        <NotificationPopPlaceholder
          isOpen={isOpen}
          onClose={handleCloseNotificationPlaceholder}
          pop={firstPopInStack as any}
        />
      )}

      {firstPopInStack && (
        <>
          {!isNotificationPlaceholder &&
            (firstPopInStack.pop_type === PopType.DEFAULT_POP ||
              firstPopInStack.pop_type === PopType.DUMMY_POP ||
              firstPopInStack.pop_type === PopType.LINK_POP) &&
            isOpen && (
              <DefaultPop
                isOpen={isOpen}
                onClose={onClose}
                firstPopInStack={firstPopInStack}
                handleSubmit={handleSubmit}
                acknowledgeLinkPop={acknowledgeLinkPop}
              />
            )}

          {!isNotificationPlaceholder &&
            (firstPopInStack.pop_type === PopType.RE_POP || firstPopInStack.pop_type === PopType.FAKE_POP) &&
            isOpen && (
              <RePop isOpen={isOpen} onClose={onClose} firstPopInStack={firstPopInStack} handleSubmit={handleSubmit} />
            )}

          {!isNotificationPlaceholder && firstPopInStack.pop_type === PopType.CHAT_GPT_POP && isOpen && (
            <GPTPop
              isOpen={isOpen}
              popText={firstPopInStack.pop_body}
              popInitiator={firstPopInStack.pop_initiator}
              handleSubmit={handleSubmit}
            />
          )}

          {!isNotificationPlaceholder && firstPopInStack.pop_type === PopType.AI_POP && isOpen && (
            <AIPop isOpen={isOpen} handleSubmit={handleSubmit} aiPop={firstPopInStack} />
          )}

          {!isNotificationPlaceholder && firstPopInStack.pop_type === PopType.AI_RE_POP && isOpen && (
            <AiRepop isOpen={isOpen} handleSubmit={handleSubmit} aiRepop={firstPopInStack} />
          )}
        </>
      )}

      {!isNotificationPlaceholder && (
        <ActionPop isOpen={isOpen} onClose={onClose} pop={firstPopInStack} handleSubmit={handleSubmit} />
      )}
    </>
  )
}
